import React, { useMemo } from "react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import "./SearchableSelect.scss";

interface SearchableSelectProps<T> {
  id?: string;
  value: string;
  valueLabel?: string;
  options?: T[];
  onChange: (value: string, option: T | null) => void;
  onSearch?: (query: string) => void;
  apiSearch?: boolean;
  getOptionLabel: (option: T) => string;
  getSelectedLabel?: (option: T) => string;
  getOptionValue: (option: T) => string;
  placeholder?: string;
  error?: boolean;
  disabled?: boolean;
  className?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onOpen?: () => void;
  loading?: boolean;
  isOptionDisabled?: (option: T) => boolean;
  disableSearch?: boolean;
  portal?: boolean;
  /** When set, a pinned "add new" entry is shown at the top of the list. */
  onCreateNew?: () => void;
  createNewLabel?: string;
}

interface InternalOption<T> {
  value: string;
  label: string;
  selectedLabel: string;
  original: T;
  isCreate?: boolean;
}

const CREATE_NEW_VALUE = "__create_new__";

const SearchableSelect = <T,>({
  id,
  value,
  valueLabel = "",
  options = [],
  onChange,
  onSearch,
  apiSearch = false,
  getOptionLabel,
  getSelectedLabel,
  getOptionValue,
  placeholder = "Select…",
  error = false,
  disabled = false,
  className = "",
  onLoadMore,
  hasMore = false,
  onOpen,
  loading = false,
  isOptionDisabled,
  disableSearch = false,
  portal = false,
  onCreateNew,
  createNewLabel = "+ Add new",
}: SearchableSelectProps<T>) => {
  const hasCreateNew = Boolean(onCreateNew);
  const internalOptions = useMemo<InternalOption<T>[]>(
    () => [
      ...(hasCreateNew
        ? [
            {
              value: CREATE_NEW_VALUE,
              label: createNewLabel,
              selectedLabel: createNewLabel,
              original: null as unknown as T,
              isCreate: true,
            },
          ]
        : []),
      ...options.map((o) => ({
        value: getOptionValue(o),
        label: getOptionLabel(o),
        selectedLabel: (getSelectedLabel ?? getOptionLabel)(o),
        original: o,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, hasCreateNew, createNewLabel],
  );

  const selectedOption = useMemo(
    () =>
      internalOptions.find((o) => o.value === value) ??
      (value && valueLabel
        ? ({
            value,
            label: valueLabel,
            selectedLabel: valueLabel,
            original: null,
          } as InternalOption<T>)
        : null),
    [internalOptions, value, valueLabel],
  );

  const styles = useMemo<StylesConfig<InternalOption<T>>>(
    () => ({
      control: (base, { isFocused, isDisabled }) => ({
        ...base,
        border: error
          ? "1px solid var(--red, #a32d2d)"
          : isFocused
            ? "1px solid var(--b)"
            : "1px solid var(--bd)",
        borderRadius: "7px",
        boxShadow:
          error && isFocused
            ? "0 0 0 3px var(--red-bg, #fcebeb)"
            : isFocused
              ? "0 0 0 3px #e6f0f8"
              : "none",
        minHeight: "38px",
        height: "38px",
        fontSize: "13px",
        background: isDisabled ? "var(--bg, #f4f5f6)" : "var(--bg2)",
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
        ":hover": {
          border: error
            ? "1px solid var(--red, #a32d2d)"
            : isFocused
              ? "1px solid var(--b)"
              : "1px solid var(--bd)",
        },
      }),
      option: (base, { isSelected, data }) => ({
        ...base,
        fontFamily: "system-ui, sans-serif",
        fontSize: "12px",
        overflowWrap: "anywhere" as const,
        ...(data.isCreate && {
          color: "var(--b)",
          fontWeight: 600,
          borderBottom: "1px solid var(--bd, #dde1e6)",
        }),
        backgroundColor: isSelected ? "var(--b)" : "transparent",
        color: isSelected ? "#fff" : base.color,
        cursor: "pointer",
        ":hover": {
          backgroundColor: isSelected
            ? "var(--b)"
            : "color-mix(in srgb, var(--b) 15%, transparent)",
        },
        ":active": { backgroundColor: "var(--b)", color: "#fff" },
      }),
      valueContainer: (base) => ({ ...base, height: "38px", padding: "0 8px" }),
      indicatorsContainer: (base) => ({ ...base, height: "38px" }),
      singleValue: (base, { selectProps, isDisabled }) => ({
        ...base,
        fontSize: "12px",
        color:
          isDisabled || selectProps.menuIsOpen
            ? "var(--tx3, #909399)"
            : "var(--tx)",
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: "12px",
        color: "var(--tx3, #909399)",
      }),
      input: (base) => ({
        ...base,
        fontSize: "12px",
        margin: 0,
        padding: 0,
        boxShadow: "none",
      }),
      menu: (base) => ({
        ...base,
        borderRadius: "7px",
        border: "1px solid var(--bd, #dde1e6)",
        boxShadow: "0 4px 18px rgba(0,0,0,0.1)",
        zIndex: 9999,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      noOptionsMessage: (base) => ({ ...base, fontSize: "12px" }),
      menuList: (base) => ({
        ...base,
        padding: "4px 0",
        maxHeight: "220px",
        borderRadius: "7px",
      }),
    }),
    [error],
  );

  const handleChange = (option: SingleValue<InternalOption<T>>) => {
    if (option?.isCreate) {
      onCreateNew?.();
      return;
    }
    onChange(option ? option.value : "", option ? option.original : null);
  };

  return (
    <ReactSelect<InternalOption<T>>
      classNamePrefix="rs"
      className={className}
      inputId={id}
      options={internalOptions}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isDisabled={disabled}
      isSearchable={!disableSearch}
      isLoading={loading}
      isClearable
      filterOption={
        apiSearch
          ? () => true
          : hasCreateNew
            ? (c, input) =>
                Boolean(c.data.isCreate) ||
                c.label.toLowerCase().includes(input.toLowerCase())
            : undefined
      }
      onMenuOpen={() => {
        if (apiSearch && onSearch) onSearch("");
        onOpen?.();
      }}
      onInputChange={(inputValue, { action }) => {
        if (action === "input-change" && apiSearch && onSearch)
          onSearch(inputValue);
      }}
      onMenuScrollToBottom={hasMore && onLoadMore ? onLoadMore : undefined}
      isOptionDisabled={
        isOptionDisabled ? (o) => isOptionDisabled(o.original) : undefined
      }
      formatOptionLabel={(option, { context }) =>
        context === "value" ? option.selectedLabel : option.label
      }
      loadingMessage={() => "Loading..."}
      noOptionsMessage={() => "No result found"}
      menuPlacement="auto"
      menuPortalTarget={portal ? document.body : undefined}
      menuPosition={portal ? "fixed" : undefined}
      styles={styles}
    />
  );
};

export default SearchableSelect;
